import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { careTokenHeaders } from '@/lib/care-token';

interface BreakButtonProps {
  hubId: string;
}

const SUPPORT_MESSAGES = [
  'Take a breath. Find a quiet spot.',
  'Lower the noise around you.',
  'Move to a calm, familiar space.',
  'Deep breaths in and out slowly.',
  'Reduce demands for a few minutes.',
  'Focus on one comforting thing.',
];

const REGULATION_TOOLS = [
  { emoji: '⏱️', label: '5-minute break', desc: 'Step away and reset' },
  { emoji: '🎵', label: 'Calming music', desc: 'Quiet, familiar sounds' },
  { emoji: '🤗', label: 'Deep pressure', desc: 'Weighted blanket or hug' },
  { emoji: '🤫', label: 'Quiet space', desc: 'Reduce sensory input' },
  { emoji: '🧸', label: 'Fidget tool', desc: 'Something to squeeze or spin' },
];

export default function BreakButton({ hubId }: BreakButtonProps) {
  const [pressed, setPressed] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [notificationStatus, setNotificationStatus] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');
  const { toast } = useToast();
  const messageIndex = Math.floor(Date.now() / 60000) % SUPPORT_MESSAGES.length;

  const handleBreakPress = async () => {
    if (pressed) return;
    setPressed(true);
    setShowSupport(true);
    setNotificationStatus('sending');

    try {
      const response = await fetch('/api/care/break-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...careTokenHeaders(hubId) },
        body: JSON.stringify({ hubId, eventId: crypto.randomUUID() }),
      });
      if (!response.ok) throw new Error(`Break request failed: ${response.status}`);
      setNotificationStatus('sent');
    } catch (error) {
      console.error('[Care View] Failed to send break request:', error);
      setNotificationStatus('failed');
      toast({
        title: 'Could not notify your caregiver',
        description: 'Please try again or ask for help another way.',
        variant: 'destructive',
      });
    }
  };

  const handleDialogOpenChange = (open: boolean) => {
    setShowSupport(open);
    if (!open) {
      setPressed(false);
      setNotificationStatus('idle');
    }
  };

  return (
    <>
      {/* Floating Break Button — respects iOS/Android safe-area-inset-bottom */}
      <div
        className="fixed right-4 z-50"
        style={{ bottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))' }}
      >
        <Button
          size="lg"
          onClick={handleBreakPress}
          className={`shadow-xl rounded-2xl px-6 py-4 text-base font-bold transition-all ${
            pressed
              ? 'bg-amber-500 hover:bg-amber-600 text-white ring-4 ring-amber-200'
              : 'bg-amber-500 hover:bg-amber-600 text-white ring-2 ring-amber-300 hover:ring-4'
          }`}
          style={{ minHeight: '60px' }}
        >
          🙋 I Need a Break
        </Button>
      </div>

      {/* Support suggestions dialog */}
      <Dialog open={showSupport} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="sm:max-w-sm text-center rounded-3xl">
          <DialogHeader>
            <DialogTitle className="sr-only">Break support</DialogTitle>
            <DialogDescription className="sr-only">
              Calming suggestions and the status of your caregiver notification.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-5">
            {/* Supportive message */}
            <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4">
              <p className="text-2xl mb-2">🌟</p>
              <p className="text-amber-800 font-semibold text-base">
                {SUPPORT_MESSAGES[messageIndex]}
              </p>
              <p className="text-amber-600 text-xs mt-1" aria-live="polite">
                {notificationStatus === 'sending' && 'Notifying your caregiver…'}
                {notificationStatus === 'sent' && 'Your caregiver has been notified.'}
                {notificationStatus === 'failed' && 'We could not notify your caregiver. Please try again.'}
              </p>
            </div>

            {/* Regulation tools */}
            <div>
              <p className="text-sm font-medium text-gray-600 mb-3">Try one of these:</p>
              <div className="space-y-2">
                {REGULATION_TOOLS.map(tool => (
                  <div key={tool.label} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl text-left">
                    <span className="text-2xl">{tool.emoji}</span>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">{tool.label}</p>
                      <p className="text-xs text-gray-500">{tool.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <Button onClick={() => handleDialogOpenChange(false)}
              variant="outline" className="w-full rounded-xl">
              I'm okay now
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
